import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import { Box, Chip, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Tooltip, Typography } from "@mui/material";
import moment from "moment";
import { useEffect, useState } from "react";
import { LoadingComp } from "../../../shared/components/loading/Loading";
import { Environment } from "../../../shared/environment";
import { ISinisterValues, SinisterService } from "../../../shared/services/api/sinister/SinisterService";
import { globalBackground, globalColorText, globalTranslate } from "../../../shared/utils";

interface Props {
	sinisterId: string;
}

const normalizeStatusForSoftChip = (status: string) => {
	if (status === "approved" || status === "accepted") return "issued";
	if (status === "provision") return "provisioned";
	if (status === "analysis") return "pending";
	return status;
};

const isEligibleForNegotiation = (status: string) => status === "approved";

export const TableCreditRecoveryValues = ({ sinisterId }: Props) => {
	const [isLoading, setIsLoading] = useState(false);
	const [rows, setRows] = useState<ISinisterValues[]>([]);
	const [totalCount, setTotalCount] = useState(0);
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(Environment.LIMITE_LINHAS_VALORES_BORDEROS);

	useEffect(() => {
		setIsLoading(true);
		SinisterService.getAllValues(page + 1, "", "", "", sinisterId, rowsPerPage).then((result) => {
			if (result instanceof Error) {
				alert(result.message);
			} else {
				setTotalCount(result.totalCount);
				setRows(result.data);
			}
			setIsLoading(false);
		});
	}, [page, rowsPerPage, sinisterId]);

	return (
		<>
			<LoadingComp isopen={isLoading} />
			<TableContainer component={Paper} elevation={0} variant="elevation" sx={{ borderRadius: 2, overflow: "hidden" }}>
				<Table size="small" stickyHeader aria-label="Tabela de valores">
					<TableHead>
						<TableRow sx={{ backgroundColor: "grey.100" }}>
							<TableCell>
								<Typography variant="button">Débito</Typography>
							</TableCell>
							<TableCell align="right">
								<Typography variant="button">Valor</Typography>
							</TableCell>
							<TableCell align="right">
								<Typography variant="button">Aprovado</Typography>
							</TableCell>
							<TableCell align="center">
								<Typography variant="button">Vencimento</Typography>
							</TableCell>
							<TableCell align="center">
								<Typography variant="button">Lançamento</Typography>
							</TableCell>
							<TableCell>
								<Typography variant="button">Status</Typography>
							</TableCell>
							<TableCell align="center">
								<Typography variant="button">Elegível</Typography>
							</TableCell>
						</TableRow>
					</TableHead>

					<TableBody>
						{rows.map((row) => (
							<TableRow key={row.id} hover>
								<TableCell>
									<Stack spacing={0.25}>
										<Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.2 }}>
											{globalTranslate(row.typeId)}
										</Typography>
										<Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.2 }}>
											{row.reference}
										</Typography>
									</Stack>
								</TableCell>
								<TableCell align="right">
									<Typography variant="body2" fontWeight={500} color="text.secondary" sx={{ lineHeight: 1.2 }}>
										{Number(row.value).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
									</Typography>
								</TableCell>
								<TableCell align="right">
									<Typography variant="body2" fontWeight={500} color="text.secondary" sx={{ lineHeight: 1.2 }}>
										{Number(row.valueApproved).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
									</Typography>
								</TableCell>
								<TableCell align="center">
									<Typography variant="body2" sx={{ lineHeight: 1.2 }}>
										{moment(row.dueDate).format("L")}
									</Typography>
								</TableCell>
								<TableCell align="center">
									<Typography variant="body2" sx={{ lineHeight: 1.2 }}>
										{moment((row as any).createdAt).format("L")}
									</Typography>
								</TableCell>
								<TableCell>
									<Chip
										size="medium"
										label={globalTranslate(row.status)}
										sx={{
											color: globalColorText(normalizeStatusForSoftChip(row.status)),
											backgroundColor: globalBackground(normalizeStatusForSoftChip(row.status)),
											fontWeight: 500,
											border: "0.5px solid #f1f1f1",
										}}
									/>
								</TableCell>
								<TableCell align="center">
									{isEligibleForNegotiation(row.status) ? (
										<Tooltip title="Elegível para negociação">
											<CheckCircleOutlinedIcon fontSize="small" color="success" />
										</Tooltip>
									) : (
										<Tooltip title="Não elegível para negociação">
											<CancelOutlinedIcon fontSize="small" color="disabled" />
										</Tooltip>
									)}
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>

				{totalCount > 0 && (
					<Box display="flex" justifyContent="flex-end" alignItems="center" mt={2} pr={2}>
						<TablePagination
							component="div"
							count={totalCount}
							page={page}
							rowsPerPage={rowsPerPage}
							onPageChange={(_, newPage) => setPage(newPage)}
							onRowsPerPageChange={(e) => {
								setRowsPerPage(parseInt(e.target.value, 10));
								setPage(0);
							}}
							rowsPerPageOptions={[10, 25, 50]}
							labelRowsPerPage="Linhas por página:"
							labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count} registros`}
						/>
					</Box>
				)}
			</TableContainer>
		</>
	);
};
